import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { Asset, Location, LocationHierarchyNode, LocationInput, LocationPaginatedResponse } from '../../types/asset.types';
import { ApiEndpoints } from '../../services/ApiUrls'; // update path as needed
import { assetApi } from './assetApi';

export interface LocationUpdateInput extends Partial<LocationInput> {
  updateReason?: string;
}

export interface LocationFilter {
  siteId: string | 'all';  // Allow 'all' as a special value
  search?: string;
  locationType?: string;
  parentLocationId?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  searchParams?: Record<string, string | boolean>;
  dropdown?: boolean;
  cf?: boolean;  // Enable the runtime (schema-driven) filter backend for column-level filters
  // Allow any additional dynamic filter parameters for schema-driven filtering
  [key: string]: any;
}

export interface LocationHierarchyFilter {
  siteId: string;
  rootLocationId?: string;
  includeInactive?: boolean;
}

export interface LocationsByHierarchyFilter {
  hierarchyPath: string;
}

export interface LocationWithTree extends Location {
  location_tree?: LocationWithTree; // recursive type
  children?: LocationWithTree[]; // optional for child nodes
}

// Assets embed the location name/hierarchy but are cached in a separate api slice,
// so a location change must invalidate them explicitly.
const invalidateAssetCaches = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
    dispatch(assetApi.util.invalidateTags(['Asset']));
  } catch {
    // Mutation failed — nothing to invalidate
  }
};

// Cache: API-level keepUnusedDataFor applies to all endpoints. Use endpoint-level override for
// session/stable data (e.g. 300s) or shorter cache for frequently changing dropdown data (e.g. 60s).
export const locationApi = createApi({
  reducerPath: 'locationApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Location', 'LocationHierarchy', 'LocationAssets'],
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({

    getLocationTypes: builder.query<
      { value: string; label: string }[],
      { dropdown?: boolean } | void
    >({
      query: (filter) => {
        const params = new URLSearchParams();
        if (filter?.dropdown !== undefined) {
          params.append('dropdown', filter.dropdown.toString());
        }
        return {
          url: ApiEndpoints.LocationrtkQuery.locations.types, // you should map this to `/location-types/`
          params,
        };
      },
      providesTags: ['Location'], // optional
    }),

    getParentLocationTypes: builder.query<
      { value: string; label: string }[],
      { location_type?: string } | void
    >({
      query: (filter) => {
        const params = new URLSearchParams();
        if (filter?.location_type) {
          params.append('location_type', filter.location_type);
        }
        return {
          url: ApiEndpoints.LocationrtkQuery.locations.parentTypes,
          params,
        };
      },
      providesTags: ['Location'],
    }),

    getLocations: builder.query<LocationPaginatedResponse, LocationFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        // Only append site_id if it's not 'all'
        if (filter.siteId !== 'all') {
          params.append('site', filter.siteId);
        }
        if (filter.search) params.append('search', filter.search);
        if (filter.locationType) params.append('location_type', filter.locationType);
        if (filter.parentLocationId) params.append('parent_location_id', filter.parentLocationId);
        if (filter.dropdown !== undefined) params.append('dropdown', filter.dropdown.toString());
        if (filter.cf) params.append('cf', 'true');
        if (filter.page !== undefined) params.append('page', filter.page.toString());
        if (filter.limit !== undefined) params.append('limit', filter.limit.toString());
        if (filter.ordering && filter.ordering.trim() !== '') params.append('ordering', filter.ordering);
        if (filter.searchParams  && Object.keys(filter.searchParams).length > 0) {
          Object.entries(filter.searchParams).forEach(([key, value]) => {
            if (value) params.append(key, value?.toString());
          });
        }
        return { url: ApiEndpoints.LocationrtkQuery.locations.list, params };
      },
      providesTags: ['Location'],
    }),

    getAllLocations: builder.query<
      LocationPaginatedResponse,
      { search?: string; limit?: number; offset?: number; page?: number; dropdown?: boolean; include_tree?: boolean; order_by?: string }
    >({
      query: (paramsObj = { limit: 10, page: 1 }) => {
        const params = new URLSearchParams();
        if (paramsObj.page) params.append('page', paramsObj.page.toString());
        if (paramsObj.limit) params.append('limit', paramsObj.limit.toString());
        if (paramsObj.search) params.append('search', paramsObj.search);
        if (paramsObj.offset) params.append('offset', paramsObj.offset.toString());
        if (paramsObj.dropdown) params.append('dropdown', paramsObj.dropdown.toString());
        if (paramsObj.include_tree) params.append('include_tree', paramsObj.include_tree.toString());
        if (paramsObj.order_by) params.append('order_by', paramsObj.order_by);

        return { url: ApiEndpoints.LocationrtkQuery.locations.list, params };
      },
      providesTags: ['Location'],
    }),

    // Dropdown-specific endpoint (dedicated backend LocationDropdownListView; same filters as LocationFilter)
    getLocationsDropdown: builder.query<
      LocationPaginatedResponse,
      {
        search?: string;
        limit?: number;
        offset?: number;
        page?: number;
        order_by?: string;
        site?: string;
        siteId?: string | 'all';
        location_type?: string;
        parent_location?: string;
        parentLocationId?: string;
        parent_ascendants?: string;
        include_tree?: boolean;
      }
    >({
      query: (paramsObj = { limit: 10, page: 1 }) => {
        const params = new URLSearchParams();
        if (paramsObj.page) params.append('page', paramsObj.page.toString());
        if (paramsObj.limit) params.append('limit', paramsObj.limit.toString());
        if (paramsObj.search) params.append('search', paramsObj.search);
        if (paramsObj.offset) params.append('offset', paramsObj.offset.toString());
        if (paramsObj.order_by) params.append('order_by', paramsObj.order_by);
        const site = paramsObj.site ?? (paramsObj.siteId !== 'all' && paramsObj.siteId ? paramsObj.siteId : undefined);
        if (site) params.append('site', site);
        if (paramsObj.location_type) params.append('location_type', paramsObj.location_type);
        const parentId = paramsObj.parent_location ?? paramsObj.parentLocationId;
        if (parentId) params.append('parent_location', parentId);
        if (paramsObj.parent_ascendants) params.append('parent_ascendants', paramsObj.parent_ascendants);
        if (paramsObj.include_tree) params.append('include_tree', paramsObj.include_tree.toString());
        params.append('paginate', 'false');

        return { url: ApiEndpoints.LocationrtkQuery.locations.dropdown, params };
      },
      providesTags: ['Location'],
    }),

    /*getLocationById: builder.query<Location, string>({x
      query: (id) => ApiEndpoints.LocationrtkQuery.locations.detail(id),
      providesTags: (result, error, id) => [{ type: 'Location', id }],
    }),*/

    getLocationById: builder.query<LocationWithTree, string>({
      query: (id) => ApiEndpoints.LocationrtkQuery.locations.detail(id) + '?include_tree=true',
      providesTags: (result, error, id) => [{ type: 'Location', id }],
    }),

    createLocation: builder.mutation<Location, LocationInput>({
      query: (location) => ({
        url: ApiEndpoints.LocationrtkQuery.locations.list,
        method: 'POST',
        body: location,
      }),
      invalidatesTags: ['Location', 'LocationHierarchy'],
      onQueryStarted: invalidateAssetCaches,
    }),

    updateLocation: builder.mutation<Location, { id: string; location: LocationUpdateInput, changeReason?: string }>({
      query: ({ id, location, changeReason }) => ({
        url: ApiEndpoints.LocationrtkQuery.locations.detail(id),
        method: 'PUT',
        body: {...location, change_reason: changeReason},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Location', id },
        'Location',
        'LocationHierarchy',
      ],
      onQueryStarted: invalidateAssetCaches,
    }),

    deleteLocation: builder.mutation<void, string>({
      query: (id) => ({
        url: ApiEndpoints.LocationrtkQuery.locations.detail(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Location', id },
        'Location',
        'LocationHierarchy',
      ],
      onQueryStarted: invalidateAssetCaches,
    }),

    getLocationHierarchy: builder.query<LocationHierarchyNode[], LocationHierarchyFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        params.append('site_id', filter.siteId);
        if (filter.rootLocationId) params.append('root_location_id', filter.rootLocationId);
        if (filter.includeInactive !== undefined) params.append('includeInactive', filter.includeInactive.toString());

        return { url: ApiEndpoints.LocationrtkQuery.locations.hierarchy, params };
      },
      providesTags: ['LocationHierarchy'],
    }),

    getLocationAssets: builder.query<Asset[], string>({
      query: (locationId) => ApiEndpoints.LocationrtkQuery.locations.assets(locationId),
      providesTags: (_result, _error, locationId) => [{ type: 'LocationAssets', id: locationId }],
    }),

    getLocationsByHierarchy: builder.query<Location[], LocationsByHierarchyFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        params.append('hierarchyPath', filter.hierarchyPath);
        return { url: ApiEndpoints.LocationrtkQuery.locations.byHierarchy, params };
      },
      providesTags: ['Location'],
    }),
  }),
});

// Export hooks
export const {
  useGetLocationsQuery,
  useGetAllLocationsQuery,
  useGetLocationsDropdownQuery,
  useGetLocationByIdQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useGetLocationHierarchyQuery,
  useGetLocationAssetsQuery,
  useGetLocationsByHierarchyQuery,
  useGetLocationTypesQuery,
  useGetParentLocationTypesQuery,
} = locationApi;
